import { Component, OnDestroy, OnInit } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from "chart.js";
import { PhaseMilestone, waterfallChartData, Phase } from '../../../interfaces/output-interfaces';
import { EstimateService } from 'projects/estimate/src/app/estimate.service';
import { Observable, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';

Chart.register(...registerables);

@Component({
  standalone: true,
  selector: 'app-estimate-chart',
  templateUrl: './estimate-chart.component.html',
  styleUrls: ['./estimate-chart.component.scss'],
  imports: [
    CommonModule,
    MatSlideToggleModule,
    FormsModule
  ]
})
export class EstimateChartComponent implements OnInit, OnDestroy {

  private _waterfallChartData: waterfallChartData = {
    data: [],
    labels: [],
    title: ""
  };
  private _barChart: any;
  public isParallel = false;
  public disableGantChart$: Observable<boolean> = this.progress.disableGantChart.asObservable();
  private tabChangeSub: Subscription;

  constructor(
    private progress: EstimateService
  ) { }

  ngOnInit(): void {
    this.tabChangeSub = this.progress.tabChange.subscribe(
      (tabChange) => {
        if (tabChange === 3) {
          this.displayChart();
        }
      }
    )
  }

  // Chart functions
  chartSlide(): void {
    this.displayChart();
  }

  displayChart(): void {
    this.resetWaterfallChartData();
    this.gantWaterfallData();
    this.createWaterfallChart();
  }

  resetWaterfallChartData(): void {
    this._waterfallChartData = {
      data: [],
      labels: [],
      title: ""
    };
  }

  gantWaterfallData(): void {
    const maxPosition = this.progress.displayedPhaseArray.phases.reduce((count, phase) => count + phase.phaseMilestones.length, 0);
    let timeCount = 0;
    let currentPosition = 0;
    let previousMilestone: PhaseMilestone;

    this.progress.displayedPhaseArray.phases.forEach((phase: Phase) => {
      phase.phaseMilestones.forEach((milestone: PhaseMilestone) => {
        const data = this.getChartData(phase, milestone, maxPosition, currentPosition, timeCount);
        const labels = `${milestone.milestoneId} ${milestone.milestoneTitle}`;

        this._waterfallChartData.data.push(data);
        this._waterfallChartData.labels.push(labels);

        if (!previousMilestone?.synergies?.includes(milestone.milestoneId) || !this.isParallel) {
          const time = this.progress.extractTime(milestone.milestoneTime);
          timeCount += this.progress.convertToDays(time.integer, time.unit)
        }
        currentPosition++;
        previousMilestone = milestone;
      });
    });
  }

  createWaterfallChart(): void {
    const canvas = document.getElementById("waterfall-chart") as HTMLCanvasElement;
    if (!canvas) return;

    if (!this._barChart && this._waterfallChartData.data.length > 0) {
      this.drawChartOnCanvas(canvas);
    } else {
      this._barChart.data.labels = this._waterfallChartData.labels;
      this._barChart.data.datasets = this._waterfallChartData.data;
      this._barChart.update(); // This will animate the change
    }
  }

  getChartData(phase: Phase, milestone: PhaseMilestone, maxPosition: number, currentPosition: number, timeCount: number) {
    const data = {
      label: phase.phaseTitle,
      data: new Array(maxPosition).fill(null),
      skipNull: true,
      backgroundColor: this.getRiskLevelColor(phase.phaseRisk?.riskLevel)
    };
    const time = this.progress.extractTime(milestone.milestoneTime);
    data.data[currentPosition] = [timeCount, timeCount + this.progress.convertToDays(time.integer, time.unit)];

    return data;
  }

  drawChartOnCanvas(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    this._barChart = new Chart(ctx, this.getChartOptions());
  }

  getChartOptions(): ChartConfiguration {
    return {
      type: 'bar',
      data: {
        labels: this._waterfallChartData.labels,
        datasets: this._waterfallChartData.data
      },
      options: {
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              callback: (value) => `Day ${value}`
            }
          },
          y: {
            grid: {
              display: false
            }
          }
        },
        responsive: true,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false,
            position: 'top',
          },
          title: {
            display: false,
            text: this._waterfallChartData.title
          },
          tooltip: {
            callbacks: {
              title: (context) => {
                return `Milestone: ${context[0].label}`;
              },
              label: () => {
                return '';
              },
              footer: (context) => {
                return `Phase: ${context[0].dataset.label}\nDays: ${context[0].raw[1] - context[0].raw[0]}`;
              }
            }
          }
        }
      }
    };
  }

  getRiskLevelColor(riskLevel: string | undefined): string {
    const riskLevelColors: Record<string, string> = {
      'high': '#DA291C',
      'medium': '#FFCD00',
      'low': '#C4D600'
    };
    return riskLevelColors[riskLevel] || '';
  }

  ngOnDestroy(): void {
    this._barChart.destroy();
    this.tabChangeSub.unsubscribe();
  }

}
